import { IJsonModel, IJsonRowNode, IJsonTabSetNode } from "flexlayout-react";

import getPanelsLayout from "./get_panels_layout";
import PanelType, { getPanelNodesByPanelType } from "../../models/domain/enums/panel_type";
import { InspectionMode } from "../../models/domain/enums/inspection_modes";
import { PanelFlags } from "../../models/domain/enums/panel_flag";

export type ModePanelConfiguration = {
  /** The tabs that share the Mode panel's tabset, which the design calls "shared with mode". */
  modeTabset: PanelType[],
  photos: boolean
};

// Resolved through a call rather than a lookup table: panel_type imports this module back for
// getDefaultPanelLayout, so PanelType is still empty while this module is first evaluated.
export function getPanelConfigurationForMode(inspectionMode: InspectionMode): ModePanelConfiguration {
  switch (inspectionMode) {
    case InspectionMode.inspect: {
      return {
        modeTabset: [PanelType.INSPECT_PANEL, PanelType.ELEMENT_GROUPS, PanelType.ELEMENT_INSPECTOR],
        photos: false
      };
    }
    case InspectionMode.bimPrep: {
      return {
        modeTabset: [PanelType.INSPECT_PANEL, PanelType.ELEMENT_GROUPS, PanelType.ELEMENT_INSPECTOR],
        photos: true
      };
    }
    // Visual and Progress share a panel set, and an unrecognised mode falls back to it.
    default: {
      return {
        modeTabset: [PanelType.INSPECT_PANEL, PanelType.ELEMENT_INSPECTOR],
        photos: true
      };
    }
  }
}

function modeTabsetPanels(configuration: ModePanelConfiguration, flags: PanelFlags): PanelType[] {
  return configuration.modeTabset.filter(panelType => panelType !== PanelType.ELEMENT_GROUPS || flags.groupsPanel);
}

export default function getPanelsLayoutForMode(inspectionMode: InspectionMode, flags: PanelFlags, tabEnableClose: boolean = true): IJsonModel {
  // A photos-only project has no BIM to configure per mode, so it keeps the layout it has today.
  if (flags.photosOnly) {
    return getPanelsLayout(flags, tabEnableClose);
  }

  const configuration = getPanelConfigurationForMode(inspectionMode);
  const modeTabset: IJsonTabSetNode = {
    type: "tabset",
    weight: 30,
    children: modeTabsetPanels(configuration, flags).map(getPanelNodesByPanelType),
    tabLocation: "top"
  };

  // flexlayout alternates orientation with nesting, so this row stacks Photos under the BIM
  // rather than adding a fourth column.
  const bimAndPhotos: IJsonRowNode | IJsonTabSetNode = configuration.photos ? {
    type: "row",
    weight: 70,
    children: [
      {
        type: "tabset",
        weight: 60,
        children: [getPanelNodesByPanelType(PanelType.VIEWER)]
      },
      {
        type: "tabset",
        weight: 40,
        children: [getPanelNodesByPanelType(PanelType.PHOTO_VIEWER)],
        tabLocation: "top"
      }
    ]
  } : {
    type: "tabset",
    weight: 70,
    children: [getPanelNodesByPanelType(PanelType.VIEWER)]
  };

  return {
    global: { tabEnableClose },
    borders: [],
    layout: {
      type: "row",
      weight: 100,
      children: [modeTabset, bimAndPhotos, {
        type: "tabset",
        weight: 30,
        children: [getPanelNodesByPanelType(PanelType.MINIMAP)],
        tabLocation: "top"
      }]
    }
  };
}
